/**
 * Pagina /superadmin/deploys — Sprint 16 S16-11.
 *
 * Mantenedor read-only de la auditoria de deploys del versionado por
 * modulo funcional (opcion C decidida 2026-05-22). Lista los deploys
 * registrados por el hook CI/CD (S16-07) en orden deployed_at DESC.
 *
 * Filtros: modulo (selector) + rango de fechas. Default ultimos 100.
 *
 * RBAC: require_super_admin.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  listarDeploys,
  type DeployFiltros,
  type DeployRead,
  type ModuloFuncional,
} from '../../lib/api/version';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { InputFecha } from '../../components/ui/InputFecha';

const ETIQUETA_MODULO: Record<ModuloFuncional, string> = {
  portal: 'Portal',
  administracion: 'Administración',
  tesoreria: 'Tesorería',
  comision: 'Comisión',
};

const MODULOS: ModuloFuncional[] = [
  'portal',
  'administracion',
  'tesoreria',
  'comision',
];

function formatearFecha(iso: string): string {
  // iso = YYYY-MM-DD del backend.
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

function formatearDeployedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function DeploysPage() {
  const [filtroModulo, setFiltroModulo] = useState<ModuloFuncional | ''>('');
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [deploys, setDeploys] = useState<DeployRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<DeployFiltros>(() => {
    const f: DeployFiltros = { limit: 100 };
    if (filtroModulo) f.modulo = filtroModulo;
    if (fechaDesde) f.fecha_desde = fechaDesde;
    if (fechaHasta) f.fecha_hasta = fechaHasta;
    return f;
  }, [filtroModulo, fechaDesde, fechaHasta]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listarDeploys(filtros)
      .then((data) => {
        if (cancelado) return;
        setDeploys(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar deploys`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [filtros]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Deploys"
        description="Historial de versiones deployadas por módulo funcional. Registro automático del CI/CD (S16-07). Lectura solo — el versionado es append-only."
      />

      {/* Filtros */}
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-4 flex flex-wrap items-end gap-4">
        <label className="text-sm space-y-1">
          <span className="text-text-secondary block">Módulo</span>
          <select
            value={filtroModulo}
            onChange={(e) =>
              setFiltroModulo(e.target.value as ModuloFuncional | '')
            }
            className="rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          >
            <option value="">Todos</option>
            {MODULOS.map((m) => (
              <option key={m} value={m}>
                {ETIQUETA_MODULO[m]}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1">
          <span className="text-text-secondary block">Desde</span>
          <InputFecha
            value={fechaDesde}
            onChange={setFechaDesde}
            className="rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        <label className="text-sm space-y-1">
          <span className="text-text-secondary block">Hasta</span>
          <InputFecha
            value={fechaHasta}
            onChange={setFechaHasta}
            className="rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>
      </div>

      {/* Tabla */}
      {error && (
        <div
          role="alert"
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm"
        >
          {error}
        </div>
      )}

      {!error && (
        <Table>
          <THead>
            <Th>Módulo</Th>
            <Th>Fecha</Th>
            <Th>Correlativo</Th>
            <Th>Tag</Th>
            <Th>SHA</Th>
            <Th>Deployed at</Th>
            <Th>Versión</Th>
          </THead>
          <TBody>
            {loading && (
              <Tr>
                <Td colSpan={7}>
                  <span className="text-text-muted">Cargando…</span>
                </Td>
              </Tr>
            )}
            {!loading && deploys.length === 0 && (
              <Tr>
                <Td colSpan={7}>
                  <span className="text-text-muted">
                    Sin deploys registrados con los filtros aplicados.
                  </span>
                </Td>
              </Tr>
            )}
            {!loading &&
              deploys.map((d) => (
                <Tr key={d.id}>
                  <Td>{ETIQUETA_MODULO[d.modulo]}</Td>
                  <Td>{formatearFecha(d.fecha)}</Td>
                  <Td>{String(d.correlativo).padStart(4, '0')}</Td>
                  <Td>
                    {d.tag ? (
                      <span className="font-mono text-xs">{d.tag}</span>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </Td>
                  <Td>
                    <span
                      className="font-mono text-xs"
                      title={d.sha}
                    >
                      {d.sha.slice(0, 7)}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-xs text-text-secondary">
                      {formatearDeployedAt(d.deployed_at)}
                    </span>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs">
                      {d.version_string}
                    </span>
                  </Td>
                </Tr>
              ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
